import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const CostsSubsidiesSection = () => {
  const [capacity, setCapacity] = useState(10);

  const costBreakdown = [
    { category: 'Batterij systeem', amount: 7500 },
    { category: 'Installatie', amount: 1500 },
    { category: 'Inverter/omvormer', amount: 1000 },
    { category: 'Bekabeling & materiaal', amount: 500 }
  ];

  const savingsData = [
    { year: 'Jaar 1', besparing: 800, cumulatief: 800 },
    { year: 'Jaar 2', besparing: 850, cumulatief: 1650 },
    { year: 'Jaar 3', besparing: 900, cumulatief: 2550 },
    { year: 'Jaar 4', besparing: 950, cumulatief: 3500 },
    { year: 'Jaar 5', besparing: 1000, cumulatief: 4500 },
    { year: 'Jaar 6', besparing: 1050, cumulatief: 5550 },
    { year: 'Jaar 7', besparing: 1100, cumulatief: 6650 },
    { year: 'Jaar 8', besparing: 1150, cumulatief: 7800 },
    { year: 'Jaar 9', besparing: 1200, cumulatief: 9000 },
    { year: 'Jaar 10', besparing: 1250, cumulatief: 10250 }
  ];

  const subsidies = [
    {
      name: 'ISDE subsidie',
      amount: '€0 (momenteel niet beschikbaar)',
      description: 'Subsidie voor energieopslag wordt verwacht in toekomst',
      status: 'pending'
    },
    {
      name: 'BTW teruggave',
      amount: '21% BTW terugvragen',
      description: 'Bij combinatie met zonnepanelen kunt u BTW terugvragen',
      status: 'available'
    },
    {
      name: 'Lokale regelingen',
      amount: 'Varieert per gemeente',
      description: 'Sommige gemeenten bieden extra subsidies',
      status: 'check'
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Kosten, rendement & subsidies
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Inzicht in de investering en terugverdientijd
          </p>
        </motion.div>

        {/* Cost Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl p-6 shadow-elevation-2"
          >
            <h3 className="text-xl font-bold text-foreground mb-6">Kostenoverzicht (10 kWh systeem)</h3>
            <div className="space-y-4 mb-6">
              {costBreakdown?.map((item, index) => (
                <div key={index} className="flex items-center justify-between pb-3 border-b border-gray-100 last:border-0">
                  <span className="text-muted-foreground">{item?.category}</span>
                  <span className="font-semibold text-foreground">€{item?.amount?.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-4 border-t-2 border-gray-200">
              <span className="text-lg font-bold text-foreground">Totaal investering</span>
              <span className="text-2xl font-bold text-primary">
                €{costBreakdown?.reduce((sum, item) => sum + item?.amount, 0)?.toLocaleString()}
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl p-6 shadow-elevation-2"
          >
            <h3 className="text-xl font-bold text-foreground mb-6">Jaarlijkse besparing</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={costBreakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="category" tick={{ fontSize: 12 }} angle={-15} textAnchor="end" height={80} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="amount" fill="#10b981" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* ROI Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-xl p-6 shadow-elevation-2 mb-12"
        >
          <h3 className="text-xl font-bold text-foreground mb-6">Terugverdientijd & cumulatieve besparing</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={savingsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="year" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Line type="monotone" dataKey="cumulatief" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Terugverdientijd</p>
              <p className="text-2xl font-bold text-green-600">9-11 jaar</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Besparing na 10 jaar</p>
              <p className="text-2xl font-bold text-blue-600">€10.250</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Levensduur batterij</p>
              <p className="text-2xl font-bold text-purple-600">15-20 jaar</p>
            </div>
          </div>
        </motion.div>

        {/* Subsidies */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-2xl font-bold text-foreground mb-6">Beschikbare subsidies & regelingen</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {subsidies?.map((subsidy, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-elevation-1">
                <div className="flex items-start justify-between mb-4">
                  <h4 className="font-semibold text-foreground">{subsidy?.name}</h4>
                  {subsidy?.status === 'available' && (
                    <Icon name="CheckCircle" size={20} className="text-success" />
                  )}
                  {subsidy?.status === 'pending' && (
                    <Icon name="Clock" size={20} className="text-warning" />
                  )}
                  {subsidy?.status === 'check' && (
                    <Icon name="Info" size={20} className="text-primary" />
                  )}
                </div>
                <p className="text-lg font-bold text-primary mb-2">{subsidy?.amount}</p>
                <p className="text-sm text-muted-foreground">{subsidy?.description}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CostsSubsidiesSection;